import { useFormik } from "formik";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import AsyncSearchableSelect from "components/AsyncSearchableSelect";
import Button from "components/Button";
import Card from "components/Card";
import InfoCard from "components/InfoCard";
import Tabs from "components/Tabs";
import TextInput from "components/TextInput";

import { CEILING_ORDER, MIN_LESS_THAN_MAX, validationSchema } from "./validationSchema";

import PageTemplate from "modules/common/PageTemplate";
import LongTermLimit from "modules/IAQLimit/LongTermLimit";
import ShortTermLimit from "modules/IAQLimit/ShortTermLimit";
import ThresholdLimit from "modules/IAQLimit/ThresholdLimit";

import { BaseOption, UserRole } from "entities/common";
import { IaqLimitSettings, IaqLimitSettingsPayload } from "entities/iaqLimits";
import { AssetsApi, CompaniesApi, IaqLimitsApi, PortfoliosApi } from "services/http";

import { useGuardedNavigation } from "hooks/useGuardedNavigation";

import { RootStore } from "store";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { setNavBlocked } from "store/slices/navGuardSlice";
import { showToast } from "store/slices/toastSlice";

import { QUERY_PARAMS } from "utils/constants";
import { QueryHelper } from "utils/helpers";
import { deepEqual } from "utils/helpers/deepEqual";
import { PAGES } from "utils/links";

import styles from "./style.module.scss";

const AIR_QUALITY_DEFAULTS = {
  thresholdLimit: { co2: 5000, hcho: 1, tvoc: 5.5 },
  shortTermLimit: { co2: 700, hcho: 0.08, tvoc: 3, pm25: 100, pm10: 100 },
  longTermLimit: { co2: 800, hcho: 0.01, tvoc: 0.6, pm25: 35, pm10: 150 },
};

const TAB_FIELD_KEY: Record<string, keyof typeof AIR_QUALITY_DEFAULTS> = {
  "threshold-limit": "thresholdLimit",
  "short-term-limit": "shortTermLimit",
  "long-term-limit": "longTermLimit",
};

const DEFAULT_DECIMAL_PATTERN = /^(\d+(\.\d*)?)?$/;
const FIVE_INT_THREE_FRAC = /^(\d{1,5}(\.\d{0,3})?)?$/;
const THREE_INT_TWO_FRAC = /^(\d{1,3}(\.\d{0,2})?)?$/;
const THREE_INT_TWO_FRAC_SIGNED = /^-?(\d{1,3}(\.\d{0,2})?)?$/;

const DECIMAL_INPUT_PATTERNS: Record<string, RegExp> = {
  "thresholdLimit.co2": FIVE_INT_THREE_FRAC,
  "thresholdLimit.hcho": FIVE_INT_THREE_FRAC,
  "thresholdLimit.tvoc": FIVE_INT_THREE_FRAC,
  "shortTermLimit.co2": FIVE_INT_THREE_FRAC,
  "shortTermLimit.hcho": FIVE_INT_THREE_FRAC,
  "shortTermLimit.tvoc": FIVE_INT_THREE_FRAC,
  "shortTermLimit.pm25": FIVE_INT_THREE_FRAC,
  "shortTermLimit.pm10": FIVE_INT_THREE_FRAC,
  "longTermLimit.co2": FIVE_INT_THREE_FRAC,
  "longTermLimit.hcho": FIVE_INT_THREE_FRAC,
  "longTermLimit.tvoc": FIVE_INT_THREE_FRAC,
  "longTermLimit.pm25": FIVE_INT_THREE_FRAC,
  "longTermLimit.pm10": FIVE_INT_THREE_FRAC,
  "temperature.min": THREE_INT_TWO_FRAC_SIGNED,
  "temperature.max": THREE_INT_TWO_FRAC_SIGNED,
  "humidity.recommendedMin": THREE_INT_TWO_FRAC,
  "humidity.recommendedMax": THREE_INT_TWO_FRAC,
  "humidity.neverExceedCeilingMin": THREE_INT_TWO_FRAC,
  "humidity.neverExceedCeilingMax": THREE_INT_TWO_FRAC,
};

const THERMAL_COMFORT_DEFAULTS = {
  temperature: { min: 22.5, max: 25.5 },
  humidity: { recommendedMin: 20, recommendedMax: 60, neverExceedCeilingMin: 56, neverExceedCeilingMax: 86 },
};

type AirQualityValues = typeof AIR_QUALITY_DEFAULTS;
type ThermalComfortValues = typeof THERMAL_COMFORT_DEFAULTS;
type LimitFormValues = AirQualityValues & ThermalComfortValues;

const toPayload = (values: LimitFormValues): IaqLimitSettingsPayload => ({
  threshold_limit: {
    co2: Number(values.thresholdLimit.co2),
    hcho: Number(values.thresholdLimit.hcho),
    tvoc: Number(values.thresholdLimit.tvoc),
  },
  short_term_limit: {
    co2: Number(values.shortTermLimit.co2),
    hcho: Number(values.shortTermLimit.hcho),
    tvoc: Number(values.shortTermLimit.tvoc),
    pm25: Number(values.shortTermLimit.pm25),
    pm10: Number(values.shortTermLimit.pm10),
  },
  long_term_limit: {
    co2: Number(values.longTermLimit.co2),
    hcho: Number(values.longTermLimit.hcho),
    tvoc: Number(values.longTermLimit.tvoc),
    pm25: Number(values.longTermLimit.pm25),
    pm10: Number(values.longTermLimit.pm10),
  },
  temperature: {
    min: Number(values.temperature.min),
    max: Number(values.temperature.max),
  },
  humidity: {
    recommended_min: Number(values.humidity.recommendedMin),
    recommended_max: Number(values.humidity.recommendedMax),
    ceiling_27c: Number(values.humidity.neverExceedCeilingMin),
    ceiling_19c: Number(values.humidity.neverExceedCeilingMax),
  },
});

const extractLimits = (values: LimitFormValues): LimitFormValues => ({
  thresholdLimit: values.thresholdLimit,
  shortTermLimit: values.shortTermLimit,
  longTermLimit: values.longTermLimit,
  temperature: values.temperature,
  humidity: values.humidity,
});

const fromResponse = (data: IaqLimitSettings): LimitFormValues => ({
  thresholdLimit: { ...data.threshold_limit },
  shortTermLimit: { ...data.short_term_limit },
  longTermLimit: { ...data.long_term_limit },
  temperature: { ...data.temperature },
  humidity: {
    recommendedMin: data.humidity.recommended_min,
    recommendedMax: data.humidity.recommended_max,
    neverExceedCeilingMin: data.humidity.ceiling_27c,
    neverExceedCeilingMax: data.humidity.ceiling_19c,
  },
});

const defaultLimitValues = (): LimitFormValues => ({
  thresholdLimit: { ...AIR_QUALITY_DEFAULTS.thresholdLimit },
  shortTermLimit: { ...AIR_QUALITY_DEFAULTS.shortTermLimit },
  longTermLimit: { ...AIR_QUALITY_DEFAULTS.longTermLimit },
  temperature: { ...THERMAL_COMFORT_DEFAULTS.temperature },
  humidity: { ...THERMAL_COMFORT_DEFAULTS.humidity },
});

const numify = (value: number | string): number => (typeof value === "number" ? value : Number(value));

const normalizeLimits = (values: LimitFormValues): LimitFormValues => ({
  thresholdLimit: {
    co2: numify(values.thresholdLimit.co2),
    hcho: numify(values.thresholdLimit.hcho),
    tvoc: numify(values.thresholdLimit.tvoc),
  },
  shortTermLimit: {
    co2: numify(values.shortTermLimit.co2),
    hcho: numify(values.shortTermLimit.hcho),
    tvoc: numify(values.shortTermLimit.tvoc),
    pm25: numify(values.shortTermLimit.pm25),
    pm10: numify(values.shortTermLimit.pm10),
  },
  longTermLimit: {
    co2: numify(values.longTermLimit.co2),
    hcho: numify(values.longTermLimit.hcho),
    tvoc: numify(values.longTermLimit.tvoc),
    pm25: numify(values.longTermLimit.pm25),
    pm10: numify(values.longTermLimit.pm10),
  },
  temperature: {
    min: numify(values.temperature.min),
    max: numify(values.temperature.max),
  },
  humidity: {
    recommendedMin: numify(values.humidity.recommendedMin),
    recommendedMax: numify(values.humidity.recommendedMax),
    neverExceedCeilingMin: numify(values.humidity.neverExceedCeilingMin),
    neverExceedCeilingMax: numify(values.humidity.neverExceedCeilingMax),
  },
});

const IAQLimit = () => {
  const dispatch = useAppDispatch();
  const authUser = useAppSelector((state: RootStore) => state.auth.user);

  const form = useFormik({
    initialValues: {
      companyId: "",
      portfolioId: "",
      assetId: "",
      thresholdLimit: { ...AIR_QUALITY_DEFAULTS.thresholdLimit },
      shortTermLimit: { ...AIR_QUALITY_DEFAULTS.shortTermLimit },
      longTermLimit: { ...AIR_QUALITY_DEFAULTS.longTermLimit },
      temperature: { ...THERMAL_COMFORT_DEFAULTS.temperature },
      humidity: { ...THERMAL_COMFORT_DEFAULTS.humidity },
    },
    validationSchema: validationSchema,
    onSubmit: () => undefined,
    validateOnBlur: false,
    validateOnChange: false,
  });
  const [selectedTab, setSelectedTab] = useState<string>("threshold-limit");
  const [companyLabel, setCompanyLabel] = useState<string>("");
  const [portfolioLabel, setPortfolioLabel] = useState<string>("");
  const [assetLabel, setAssetLabel] = useState<string>("");
  const formRef = useRef<HTMLFormElement>(null);
  const portfolioCompanies = useRef<Record<string, number>>({});
  const baselineRef = useRef<LimitFormValues>(extractLimits(form.values));
  const companyIdRef = useRef<string>("");
  const portfolioIdRef = useRef<string>("");
  const didInitRef = useRef<boolean>(false);

  const guardNavigation = useGuardedNavigation();

  const isLoading = false;
  const isAdmin = useMemo(() => authUser?.role === UserRole.ADMINISTRATOR, [authUser?.role]);
  const hasAsset = Boolean(form.values.assetId);

  const isDirty = !deepEqual(extractLimits(form.values), baselineRef.current);

  useEffect(() => {
    dispatch(setNavBlocked(isDirty));
  }, [isDirty, dispatch]);

  useEffect(() => () => void dispatch(setNavBlocked(false)), [dispatch]);

  const fetchFirstCompany = useCallback(async (): Promise<BaseOption | null> => {
    try {
      const { data } = await CompaniesApi.getList(
        QueryHelper.getQueryString({ [QUERY_PARAMS.PAGE]: 1, [QUERY_PARAMS.PAGE_SIZE]: 1 }),
      );
      const first = data?.items?.[0];
      return first ? { value: String(first.id), label: first.name } : null;
    } catch {
      return null;
    }
  }, []);

  const fetchFirstPortfolio = useCallback(async (companyId: string): Promise<BaseOption | null> => {
    try {
      const { data } = await PortfoliosApi.getList(
        QueryHelper.getQueryString({
          [QUERY_PARAMS.COMPANY_ID]: companyId || undefined,
          [QUERY_PARAMS.MANAGEABLE_ONLY]: true,
          [QUERY_PARAMS.PAGE]: 1,
          [QUERY_PARAMS.PAGE_SIZE]: 1,
        }),
      );
      const first = data?.items?.[0];
      if (first) {
        portfolioCompanies.current[String(first.id)] = first.company_id;
      }
      return first ? { value: String(first.id), label: first.name } : null;
    } catch {
      return null;
    }
  }, []);

  const fetchFirstAsset = useCallback(async (portfolioId: string): Promise<BaseOption | null> => {
    try {
      const { data } = await AssetsApi.getList(
        QueryHelper.getQueryString({
          [QUERY_PARAMS.PORTFOLIO_ID]: portfolioId || undefined,
          [QUERY_PARAMS.MANAGEABLE_ONLY]: true,
          [QUERY_PARAMS.PAGE]: 1,
          [QUERY_PARAMS.PAGE_SIZE]: 1,
        }),
      );
      const first = data?.items?.[0];
      return first ? { value: String(first.id), label: first.name } : null;
    } catch {
      return null;
    }
  }, []);

  const handleCompanySearch = useCallback(async (search: string, page: number) => {
    try {
      const { data } = await CompaniesApi.getList(
        QueryHelper.getQueryString({
          [QUERY_PARAMS.SEARCH]: search || undefined,
          [QUERY_PARAMS.PAGE]: page,
          [QUERY_PARAMS.PAGE_SIZE]: 50,
        }),
      );

      const options = (data?.items || []).map((company) => ({ value: String(company.id), label: company.name }));
      const hasMore = data?.total ? page * 50 < data.total : false;

      return { options, hasMore };
    } catch {
      return { options: [], hasMore: false };
    }
  }, []);

  const handlePortfolioSearch = useCallback(async (search: string, page: number) => {
    try {
      const { data } = await PortfoliosApi.getList(
        QueryHelper.getQueryString({
          [QUERY_PARAMS.COMPANY_ID]: companyIdRef.current || undefined,
          [QUERY_PARAMS.MANAGEABLE_ONLY]: true,
          [QUERY_PARAMS.SEARCH]: search || undefined,
          [QUERY_PARAMS.PAGE]: page,
          [QUERY_PARAMS.PAGE_SIZE]: 50,
        }),
      );

      (data?.items || []).forEach((item) => {
        portfolioCompanies.current[String(item.id)] = item.company_id;
      });

      const options = (data?.items || []).map((item) => ({ value: String(item.id), label: item.name }));
      const hasMore = data?.total ? page * 50 < data.total : false;

      return { options, hasMore };
    } catch {
      return { options: [], hasMore: false };
    }
  }, []);

  const handleAssetSearch = useCallback(async (search: string, page: number) => {
    if (!portfolioIdRef.current) {
      return { options: [], hasMore: false };
    }

    try {
      const { data } = await AssetsApi.getList(
        QueryHelper.getQueryString({
          [QUERY_PARAMS.PORTFOLIO_ID]: portfolioIdRef.current,
          [QUERY_PARAMS.MANAGEABLE_ONLY]: true,
          [QUERY_PARAMS.SEARCH]: search || undefined,
          [QUERY_PARAMS.PAGE]: page,
          [QUERY_PARAMS.PAGE_SIZE]: 50,
        }),
      );

      const options = (data?.items || []).map((item) => ({ value: String(item.id), label: item.name }));
      const hasMore = data?.total ? page * 50 < data.total : false;

      return { options, hasMore };
    } catch {
      return { options: [], hasMore: false };
    }
  }, []);

  const applyLimitValues = useCallback(
    (values: LimitFormValues) => {
      form.setFieldValue("thresholdLimit", values.thresholdLimit);
      form.setFieldValue("shortTermLimit", values.shortTermLimit);
      form.setFieldValue("longTermLimit", values.longTermLimit);
      form.setFieldValue("temperature", values.temperature);
      form.setFieldValue("humidity", values.humidity);
      baselineRef.current = extractLimits(values);
      dispatch(setNavBlocked(false));
    },
    [form, dispatch],
  );

  const applyAsset = useCallback(
    async (value: string, label: string) => {
      form.setFieldValue("assetId", value);
      setAssetLabel(label);

      if (!value) {
        applyLimitValues(defaultLimitValues());
        return;
      }

      try {
        const { data } = await IaqLimitsApi.getLimits(value);
        applyLimitValues(fromResponse(data));
      } catch {
        return;
      }
    },
    [form, applyLimitValues],
  );

  const selectFirstAsset = useCallback(
    async (portfolioId: string) => {
      if (!portfolioId) {
        await applyAsset("", "");
        return;
      }
      const first = await fetchFirstAsset(portfolioId);
      await applyAsset(first?.value ?? "", first?.label ?? "");
    },
    [fetchFirstAsset, applyAsset],
  );

  const applyPortfolio = useCallback(
    async (value: string, label: string) => {
      portfolioIdRef.current = value;
      form.setFieldValue("portfolioId", value);
      setPortfolioLabel(label);
      await selectFirstAsset(value);
    },
    [form, selectFirstAsset],
  );

  const selectFirstPortfolio = useCallback(
    async (companyId: string) => {
      const first = await fetchFirstPortfolio(companyId);
      await applyPortfolio(first?.value ?? "", first?.label ?? "");
    },
    [fetchFirstPortfolio, applyPortfolio],
  );

  const applyCompany = useCallback(
    async (value: string, label: string) => {
      companyIdRef.current = value;
      form.setFieldValue("companyId", value);
      setCompanyLabel(label);
      await selectFirstPortfolio(value);
    },
    [form, selectFirstPortfolio],
  );

  const handleCompanyChange = useCallback(
    (value: string, label: string) => {
      guardNavigation(() => void applyCompany(value, label));
    },
    [guardNavigation, applyCompany],
  );

  const handlePortfolioChange = useCallback(
    (value: string, label: string) => {
      guardNavigation(() => void applyPortfolio(value, label));
    },
    [guardNavigation, applyPortfolio],
  );

  const handleAssetChange = useCallback(
    (value: string, label: string) => {
      guardNavigation(() => void applyAsset(value, label));
    },
    [guardNavigation, applyAsset],
  );

  useEffect(() => {
    if (didInitRef.current || !authUser) {
      return;
    }
    didInitRef.current = true;

    void (async () => {
      if (isAdmin) {
        const company = await fetchFirstCompany();
        await applyCompany(company?.value ?? "", company?.label ?? "");
      } else {
        await selectFirstPortfolio("");
      }
    })();
  }, [authUser, isAdmin, fetchFirstCompany, applyCompany, selectFirstPortfolio]);

  const handleNumberInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name } = event.target;
      const value = event.target.value.trim();
      const pattern = DECIMAL_INPUT_PATTERNS[name] ?? DEFAULT_DECIMAL_PATTERN;

      if (!pattern.test(value)) {
        return;
      }

      const parsedValue = value === "" || value === "-" || value.endsWith(".") ? value : Number(value);

      form.setFieldValue(name, parsedValue);
    },
    [form],
  );

  const handleResetAirQuality = useCallback(() => {
    const fieldKey = TAB_FIELD_KEY[selectedTab];
    form.setFieldValue(fieldKey, AIR_QUALITY_DEFAULTS[fieldKey]);
    form.setFieldError(fieldKey, undefined);
  }, [selectedTab, form]);

  const handleResetThermalComfort = useCallback(() => {
    form.setFieldValue("temperature", THERMAL_COMFORT_DEFAULTS.temperature);
    form.setFieldValue("humidity", THERMAL_COMFORT_DEFAULTS.humidity);
    form.setFieldError("temperature", undefined);
    form.setFieldError("humidity", undefined);
  }, [form]);

  const handleSave = useCallback(async () => {
    if (!hasAsset) {
      dispatch(
        showToast({
          id: "iaq-limits-no-assets",
          message: "No portfolios or assets are available. Please create one before applying the limits.",
          type: "error",
        }),
      );
      return;
    }

    const errors = await form.validateForm();

    const erroredTab = (["threshold-limit", "short-term-limit", "long-term-limit"] as const).find(
      (tab) => errors[TAB_FIELD_KEY[tab]],
    );
    if (erroredTab && erroredTab !== selectedTab) {
      setSelectedTab(erroredTab);
    }

    if (Object.keys(errors).length > 0) {
      return;
    }

    const cleaned = normalizeLimits(form.values);

    try {
      await IaqLimitsApi.saveLimits(form.values.assetId, toPayload(cleaned));
      applyLimitValues(cleaned);
      dispatch(
        showToast({ id: "iaq-limits-saved", message: "IAQ limit settings saved successfully.", type: "success" }),
      );
    } catch {
      dispatch(
        showToast({
          id: "iaq-limits-save-failed",
          message: "Failed to save IAQ limit settings. Please try again later.",
          type: "error",
        }),
      );
    }
  }, [form, hasAsset, selectedTab, dispatch, applyLimitValues]);

  const firstCards = useMemo(
    () => [
      ...(isAdmin
        ? [
            {
              label: "Company",
              placeholder: "Select company",
              value: form.values.companyId,
              selectedLabel: companyLabel,
              onSearch: handleCompanySearch,
              onChange: handleCompanyChange,
              error: form.errors.companyId,
            },
          ]
        : []),
      {
        label: "Portfolio",
        placeholder: "Select portfolio",
        value: form.values.portfolioId,
        selectedLabel: portfolioLabel,
        onSearch: handlePortfolioSearch,
        onChange: handlePortfolioChange,
        error: form.errors.portfolioId,
      },
      {
        label: "Asset",
        placeholder: "Select asset",
        value: form.values.assetId,
        selectedLabel: assetLabel,
        onSearch: handleAssetSearch,
        onChange: handleAssetChange,
        error: form.errors.assetId,
      },
    ],
    [
      isAdmin,
      form.values.companyId,
      form.values.portfolioId,
      form.values.assetId,
      form.errors.companyId,
      form.errors.portfolioId,
      form.errors.assetId,
      companyLabel,
      handleCompanySearch,
      handleCompanyChange,
      portfolioLabel,
      handlePortfolioSearch,
      handlePortfolioChange,
      assetLabel,
      handleAssetSearch,
      handleAssetChange,
    ],
  );

  return (
    <PageTemplate
      title="IAQ limit settings"
      description="Configure thresholds and comfort ranges for all IAQ metrics."
      isBackButton
      backButtonLink={PAGES.SETTINGS}
    >
      <div className={styles.content}>
        <InfoCard size="small" variant="orange" color="orange" className={styles.warning}>
          <p className={styles.warningTitle}>Warning:</p>

          <p className={styles.warningDescription}>
            {`Changes you save here will be applied immediately to the selected asset and will affect dashboard indicators and alert notifications for that asset across all users. Please make sure your changes are intentional before saving.\n\nCompliance reports always use the standard DM-HSD-GU119 default values as the reference benchmark, regardless of any custom limits configured here.`}
          </p>
        </InfoCard>

        <form ref={formRef} onSubmit={form.handleSubmit} className={styles.cards}>
          <Card className={styles.card}>
            <p className={styles.cardTitle}>Select an asset to configure</p>

            <p className={styles.cardDescription}>
              {isAdmin
                ? "IAQ limit settings are saved separately for each asset. Select the company, then the portfolio, then the asset you want to configure."
                : "IAQ limit settings are saved separately for each asset. Select the portfolio, then the asset you want to configure."}
            </p>

            <div className={styles.firstCards}>
              {firstCards.map((card, index) => (
                <fieldset key={index} className={styles.fieldset}>
                  <p className={styles.fieldsetTitle}>
                    {index + 1}. {card.label}
                  </p>

                  <AsyncSearchableSelect
                    label={card.label}
                    placeholder={card.placeholder}
                    value={card.value}
                    selectedLabel={card.selectedLabel}
                    isDisabled={isLoading}
                    onSearch={card.onSearch}
                    onChange={card.onChange}
                    error={card.error}
                  />
                </fieldset>
              ))}
            </div>
          </Card>

          <Card className={styles.card}>
            <p className={styles.cardTitle}>Air quality parameters</p>

            <p className={styles.cardDescription}>
              Configure three limit types per metric as defined in DM-HSD-GU119. TLV-C = instant ceiling · ST =
              short-term rolling average · LT = long-term rolling average (primary compliance benchmark)
            </p>

            <Tabs
              value={selectedTab}
              onChange={setSelectedTab}
              tabs={[
                {
                  label: "Threshold limit",
                  value: "threshold-limit",
                },
                {
                  label: "Short-term limit",
                  value: "short-term-limit",
                },
                {
                  label: "Long-term limit",
                  value: "long-term-limit",
                },
              ]}
              className={styles.tabs}
            />

            {selectedTab === "threshold-limit" && (
              <ThresholdLimit
                co2={form.values.thresholdLimit.co2}
                hcho={form.values.thresholdLimit.hcho}
                tvoc={form.values.thresholdLimit.tvoc}
                errors={form.errors.thresholdLimit}
                handleFieldChange={handleNumberInputChange}
              />
            )}

            {selectedTab === "short-term-limit" && (
              <ShortTermLimit
                co2={form.values.shortTermLimit.co2}
                hcho={form.values.shortTermLimit.hcho}
                tvoc={form.values.shortTermLimit.tvoc}
                pm25={form.values.shortTermLimit.pm25}
                pm10={form.values.shortTermLimit.pm10}
                errors={form.errors.shortTermLimit}
                handleFieldChange={handleNumberInputChange}
              />
            )}

            {selectedTab === "long-term-limit" && (
              <LongTermLimit
                co2={form.values.longTermLimit.co2}
                hcho={form.values.longTermLimit.hcho}
                tvoc={form.values.longTermLimit.tvoc}
                pm25={form.values.longTermLimit.pm25}
                pm10={form.values.longTermLimit.pm10}
                errors={form.errors.longTermLimit}
                handleFieldChange={handleNumberInputChange}
              />
            )}

            <Button type="button" variant="secondary" className={styles.resetButton} onClick={handleResetAirQuality}>
              Reset all to DM-HSD-GU119 defaults
            </Button>
          </Card>

          <Card className={styles.card}>
            <p className={styles.cardTitle}>Thermal comfort</p>

            <p className={styles.cardDescription}>
              Configure recommended ranges and never-exceed ceilings per DM-HSD-GU119. Alerts trigger when values fall
              outside the configured range or exceed the ceiling.
            </p>

            <div className={styles.thermal}>
              <fieldset className={styles.fieldset}>
                <p className={styles.fieldsetTitle}>
                  <span>Temperature</span>
                  <span className={styles.fieldsetTooltip}>°C</span>
                </p>

                <div className={styles.fieldsetLabel}>
                  <span className={styles.fieldsetLabelTitle}>Recommended range</span>

                  <div className={styles.fieldsetLabelInputs}>
                    <TextInput
                      label="Min"
                      value={form.values.temperature.min}
                      onChange={handleNumberInputChange}
                      name="temperature.min"
                      error={form.errors.temperature?.min}
                    />

                    <TextInput
                      label="Max"
                      value={form.values.temperature.max}
                      onChange={handleNumberInputChange}
                      name="temperature.max"
                      error={
                        form.errors.temperature?.max ??
                        (form.errors.temperature?.min === MIN_LESS_THAN_MAX || undefined)
                      }
                    />
                  </div>

                  <span className={styles.fieldsetLabelHint}>
                    <span>!</span>

                    <span>Def: 22.5 - 25.5 °C</span>
                  </span>
                </div>
              </fieldset>

              <fieldset className={styles.fieldset}>
                <p className={styles.fieldsetTitle}>
                  <span>Humidity</span>
                  <span className={styles.fieldsetTooltip}>%</span>
                </p>

                <div className={styles.fieldsetLabel}>
                  <span className={styles.fieldsetLabelTitle}>Recommended range</span>

                  <div className={styles.fieldsetLabelInputs}>
                    <TextInput
                      label="Min"
                      value={form.values.humidity.recommendedMin}
                      onChange={handleNumberInputChange}
                      name="humidity.recommendedMin"
                      error={form.errors.humidity?.recommendedMin}
                    />

                    <TextInput
                      label="Max"
                      value={form.values.humidity.recommendedMax}
                      onChange={handleNumberInputChange}
                      name="humidity.recommendedMax"
                      error={
                        form.errors.humidity?.recommendedMax ??
                        (form.errors.humidity?.recommendedMin === MIN_LESS_THAN_MAX || undefined)
                      }
                    />
                  </div>

                  <span className={styles.fieldsetLabelHint}>
                    <span>!</span>
                    <span>Def: 20 - 60 %</span>
                  </span>
                </div>

                <div className={styles.fieldsetLabel}>
                  <span className={styles.fieldsetLabelTitle}>Temperature-adjusted never-to-exceed ceilings</span>

                  <div className={styles.fieldsetLabelInputs}>
                    <TextInput
                      label="At ≥ 27 °C"
                      value={form.values.humidity.neverExceedCeilingMin}
                      onChange={handleNumberInputChange}
                      name="humidity.neverExceedCeilingMin"
                      error={form.errors.humidity?.neverExceedCeilingMin}
                    />

                    <TextInput
                      label="At ≥ 19 °C"
                      value={form.values.humidity.neverExceedCeilingMax}
                      onChange={handleNumberInputChange}
                      name="humidity.neverExceedCeilingMax"
                      error={
                        form.errors.humidity?.neverExceedCeilingMax ??
                        (form.errors.humidity?.neverExceedCeilingMin === CEILING_ORDER || undefined)
                      }
                    />
                  </div>

                  <span className={styles.fieldsetLabelHint}>
                    <span>!</span>
                    <span>{`Def:\n56% max when temp ≥ 27°C\n86% max when temp ≥ 19°C`}</span>
                  </span>
                </div>
              </fieldset>
            </div>

            <Button
              type="button"
              variant="secondary"
              className={styles.resetButton}
              onClick={handleResetThermalComfort}
            >
              Reset all to DM-HSD-GU119 defaults
            </Button>
          </Card>

          <Button type="button" variant="primary" className={styles.saveButton} onClick={handleSave}>
            <span className={styles.saveButtonLabel}>
              {hasAsset ? `Save & apply for "${assetLabel}"` : "Save & apply"}
            </span>
          </Button>
        </form>
      </div>
    </PageTemplate>
  );
};

export default IAQLimit;
